import * as THREE from 'three';
import {
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  Object3D,
  Group,
  AnimationMixer,
  Vector2,
  Color,
  OrthographicCamera,
} from 'three';

/**
 * Three.js 렌더링을 담당하는 Render 클래스
 * Scene과 WebGLRenderer를 효율적으로 관리하고 렌더링 성능을 최적화합니다.
 */
export class Render {
  // Scene 관련 속성
  public renderer!: WebGLRenderer;
  public camera!: PerspectiveCamera | OrthographicCamera;

  // 애니메이션 관리
  private _animationMixers: AnimationMixer[] = [];

  // 렌더링 상태 관리
  private _isRendering: boolean = false;
  private _isDisposed: boolean = false;
  private _frameId: number | null = null;
  private _lastRenderTime: number = 0;

  // 성능 최적화 설정
  private _frameRate: number;
  private _frameInterval: number;
  private _startTime: number;

  // 이벤트 콜백
  private _onRenderComplete?: () => void;
  private _onSceneUpdated?: (scene: Scene) => void;
  private _onRendererResized?: (size: Vector2) => void;

  // CameraManager 참조 (자동 회전을 위해)
  private _cameraManager?: any;

  constructor(
    public canvasDom: HTMLDivElement,
    public canvas: HTMLCanvasElement,
    public scene: Scene,
    camera: PerspectiveCamera | OrthographicCamera,
    frameRate: number = 60,
    startTime: number = 0,
  ) {
    this._frameRate = frameRate;
    this._frameInterval = 1000 / frameRate;
    this._startTime = startTime;

    this.initializeScene();
    this.initializeRenderer();
    this.initializeCamera(camera);
  }

  /**
   * Scene 초기화
   */
  private initializeScene(): void {
    // 기본 환경 설정
    this.scene.background = new Color(0x7b7d83);
    this.scene.fog = new THREE.Fog(0x7b7d83, 4, 10);

    if (this._onSceneUpdated) {
      this._onSceneUpdated(this.scene);
    }
  }

  /**
   * WebGLRenderer 초기화
   */
  private initializeRenderer(): void {
    try {
      this.renderer = new WebGLRenderer({
        canvas: this.canvas,
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance',
        precision: 'highp',
        stencil: true,
        depth: true,
        premultipliedAlpha: true,
        preserveDrawingBuffer: true,
      });

      this.configureRenderer();
      this.setRendererSize();
    } catch (error) {
      console.error('WebGLRenderer 초기화 실패:', error);
      throw new Error('WebGL를 지원하지 않는 브라우저입니다.');
    }
  }

  /**
   * 렌더러 설정 구성
   */
  private configureRenderer(): void {
    if (!this.renderer) return;

    // 픽셀 비율 설정
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // 그림자 설정
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // 색상 공간 및 톤 매핑 설정
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.78;

    // 기본 배경색 설정
    this.renderer.setClearColor(0x7b7d83, 0);
  }

  /**
   * 카메라 초기화 및 설정
   */
  private initializeCamera(camera: PerspectiveCamera | OrthographicCamera): void {
    this.camera = camera;
    this.camera.name = 'MainCamera';

    if (this.scene) {
      this.scene.add(this.camera);
    }
  }

  /**
   * CameraManager 설정
   */
  public setCameraManager(cameraManager: any): void {
    this._cameraManager = cameraManager;
  }

  /**
   * 렌더러 크기 설정
   */
  private setRendererSize(): void {
    if (!this.renderer || !this.canvasDom) return;

    const width = this.canvasDom.clientWidth;
    const height = this.canvasDom.clientHeight;

    this.renderer.setSize(width, height, false);
    if (this._onRendererResized) {
      this._onRendererResized(new Vector2(width, height));
    }
  }

  /**
   * 애니메이션 믹서 추가
   */
  public addAnimationMixer(mixer: AnimationMixer): void {
    if (!this._animationMixers.includes(mixer)) {
      this._animationMixers.push(mixer);
    }
  }

  /**
   * 애니메이션 믹서 제거
   */
  public removeAnimationMixer(mixer: AnimationMixer): void {
    const index = this._animationMixers.indexOf(mixer);
    if (index > -1) {
      this._animationMixers.splice(index, 1);
    }
  }

  /**
   * 단일 프레임 렌더링
   */
  public render(): void {
    if (!this.scene || !this.renderer || !this.camera || this._isDisposed) return;

    try {
      // CameraManager 자동 회전 업데이트
      if (this._cameraManager && typeof this._cameraManager.updateAutoRotateCamera === 'function') {
        this._cameraManager.updateAutoRotateCamera();
      }

      // 애니메이션 믹서 업데이트
      this.updateAnimationMixers();

      // 렌더링 실행
      this.renderer.render(this.scene, this.camera);

      // 렌더링 완료 콜백 호출
      if (this._onRenderComplete) {
        this._onRenderComplete();
      }
    } catch (error) {
      console.error('렌더링 오류:', error);
    }
  }

  /**
   * 애니메이션 믹서 업데이트
   */
  private updateAnimationMixers(): void {
    const deltaTime = this._frameInterval / 1000;
    this._animationMixers.forEach((mixer) => {
      if (mixer) {
        mixer.update(deltaTime);
      }
    });
  }

  /**
   * 애니메이션 루프 시작
   */
  public animate(): void {
    if (this._isRendering || this._isDisposed) return;

    if (!this._startTime) {
      this._startTime = performance.now();
    }

    this._isRendering = true;
    this.animateLoop();
  }

  /**
   * 애니메이션 루프
   */
  private animateLoop(): void {
    if (!this._isRendering || this._isDisposed) return;

    const currentTime = performance.now();
    const deltaTime = currentTime - this._lastRenderTime;

    if (deltaTime >= this._frameInterval) {
      this.render();
      this._lastRenderTime = currentTime;
    }

    this._frameId = requestAnimationFrame(() => this.animateLoop());
  }

  /**
   * 애니메이션 중지
   */
  public stopAnimation(): void {
    this._isRendering = false;
    if (this._frameId) {
      cancelAnimationFrame(this._frameId);
      this._frameId = null;
    }
  }

  /**
   * 렌더러 크기 조정
   */
  public resizeRenderer(width: number, height: number): void {
    if (!this.renderer || this._isDisposed) return;

    this.renderer.setSize(width, height, false);
    if (this._onRendererResized) {
      this._onRendererResized(new Vector2(width, height));
    }
  }

  /**
   * 픽셀 비율 설정
   */
  public setPixelRatio(pixelRatio: number): void {
    if (this.renderer) {
      this.renderer.setPixelRatio(Math.min(pixelRatio, 2));
    }
  }

  /**
   * 그림자 설정
   */
  public setShadowEnabled(enabled: boolean): void {
    if (this.renderer) {
      this.renderer.shadowMap.enabled = enabled;
    }
  }

  /**
   * 배경색 설정
   */
  public setBackgroundColor(color: string | number): void {
    if (this.scene) {
      this.scene.background = new Color(color);
    }
  }

  /**
   * 안개 설정
   */
  public setFog(color: string | number, near: number, far: number): void {
    if (this.scene) {
      this.scene.fog = new THREE.Fog(color, near, far);
    }
  }

  /**
   * 톤 매핑 노출 설정
   */
  public setToneMappingExposure(exposure: number): void {
    if (this.renderer) {
      this.renderer.toneMappingExposure = exposure;
    }
  }

  /**
   * Scene 가져오기
   */
  public getScene(): Scene {
    return this.scene;
  }

  /**
   * Renderer 반환
   */
  public getRenderer(): WebGLRenderer | null {
    return this.renderer;
  }

  /**
   * 애니메이션 상태 확인
   */
  public isAnimating(): boolean {
    return this._isRendering;
  }

  /**
   * 이벤트 콜백 설정
   */
  public setOnRenderComplete(callback: () => void): void {
    this._onRenderComplete = callback;
  }

  public setOnSceneUpdated(callback: (scene: Scene) => void): void {
    this._onSceneUpdated = callback;
  }

  public setOnRendererResized(callback: (size: Vector2) => void): void {
    this._onRendererResized = callback;
  }

  /**
   * Scene 정리 (렌더링 관련 리소스만)
   */
  public clearScene(): void {
    if (this.scene) {
      // 애니메이션 믹서만 정리
      this._animationMixers = [];

      // Scene은 ObjectControl에서 관리하므로 여기서는 정리하지 않음
      // 단, 렌더링 관련 설정만 초기화
      this.scene.background = new Color(0x7b7d83);
      this.scene.fog = new THREE.Fog(0x7b7d83, 6, 10);
    }
  }

  /**
   * 성능 통계 가져오기
   */
  public getPerformanceStats(): {
    frameRate: number;
    renderTime: number;
    animationMixers: number;
    sceneObjects: number;
    isAnimating: boolean;
  } {
    const frameRate = 1000 / this._frameInterval;
    const renderTime = this._lastRenderTime;
    const animationMixers = this._animationMixers.length;
    const sceneObjects = this.scene ? this.scene.children.length : 0;
    const isAnimating = this._isRendering;

    return {
      frameRate,
      renderTime,
      animationMixers,
      sceneObjects,
      isAnimating,
    };
  }

  /**
   * 리소스 정리
   */
  public dispose(): void {
    this.stopAnimation();
    this.clearScene();

    if (this.renderer) {
      this.renderer.dispose();
    }

    this._isDisposed = true;
  }
}
